import React, { useState, useCallback } from 'react';
import OtpInput from 'react-otp-input';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Button from 'partial/components/Button';
import { ModalCard, useCreateModal } from 'partial/components/Modal';
import { useMfaSetup } from 'modules/mfa/hooks';
import { useMFA } from './SetupMFA';

export const useDisableMFA = () => {
  const createModal = useCreateModal();
  const openSetupMFA = useMFA();

  return useCallback(() => {
    createModal({
      content: (close) => {
        const onSuccess = () => {
          close();
          openSetupMFA(() => {});
        };
        return <DisableMFA onClose={close} onDisableSuccess={onSuccess} />;
      },
    });
  }, [createModal, openSetupMFA]);
};

function DisableMFA({ onClose, onDisableSuccess }) {
  const [state, setState] = useState({ otp: '' });
  const [isUpdating, action] = useMfaSetup(onDisableSuccess);

  const submit = () => {
    action({ type: 'disable', ...state });
  };

  const handleChange = (val) => {
    setState({ otp: val });
  };

  return (
    <ModalCard size="md" onClose={onClose}>
      <div className="px-8 pb-8 max-h-[90vh] overflow-y-auto">
        <h4 className="heading-4 text-gray-800 uppercase mb-6">
          Disable Multi-factor Authentication
        </h4>
        <div className="space-y-4">
          <p className="text-neutral-600 text-sm">
            Enter your current 6-digit MFA PIN to disable two-factor
            authentication. You will be prompted to set up a new MFA after.
          </p>
          <div className="px-4">
            <OtpInput
              shouldAutoFocus
              inputType="tel"
              containerStyle="w-full gap-4 sm:px-10"
              value={state?.otp}
              onChange={handleChange}
              numInputs={6}
              renderInput={(props) => (
                <input
                  {...props}
                  className={classNames(
                    'text-gray-700 font-bold text-lg flex-1 rounded border border-light-gray-300 !w-full h-8 sm:h-14',
                  )}
                />
              )}
            />
          </div>
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded p-3 text-sm">
            After disabling, you will be asked to configure a new MFA device.
          </div>
        </div>
        <div className="flex flex-col gap-2 mt-6 sm:mt-8">
          <Button
            primary
            className="uppercase w-full !bg-red-600 hover:!bg-red-700 focus-visible:ring-red-600"
            disabled={isUpdating || state.otp.length < 6}
            onClick={submit}
          >
            Disable MFA
          </Button>
          <Button outline onClick={onClose} className="uppercase w-full">
            Cancel
          </Button>
        </div>
      </div>
    </ModalCard>
  );
}

DisableMFA.propTypes = {
  onClose: PropTypes.func.isRequired,
  onDisableSuccess: PropTypes.func.isRequired,
};

export default DisableMFA;
